import { tr } from "../../i18n";
import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Upload, Trash2, Eye } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "../../auth/AuthContext";
import { Badge, statusTone, Card, fmtDate, fmtDateTime, fileUrl } from "../../components/portal";
import { Field, inputCls } from "../auth/AuthLayout";
const SUMMARY_FIELDS = [["assessment_summary", "Assessment Summary"], ["eri_results", "ERI Results"], ["key_findings", "Key Findings"], ["risk_summary", "Risk Summary"], ["recommendations", "Recommendations"], ["management_summary", "Management Summary"], ["additional_notes", "Additional Notes"]];
export default function AdminAssessmentDetail() {
  const {
    lang = "en",
    id
  } = useParams();
  const [doc, setDoc] = useState(null);
  const [meta, setMeta] = useState(null);
  const [summaries, setSummaries] = useState(null);
  const [published, setPublished] = useState(null);
  const [certForm, setCertForm] = useState({
    certificate_number: "",
    certificate_type: "INDIVIDUAL",
    start_date: "",
    expiry_date: "",
    status_override: ""
  });
  const reportRef = useRef(null);
  const certRef = useRef(null);
  const load = () => api.get(`/admin/assessments/${id}`).then(r => {
    setDoc(r.data);
    setMeta({
      status: r.data.status,
      start_date: r.data.start_date || "",
      completion_date: r.data.completion_date || "",
      notes: r.data.notes || ""
    });
    setSummaries({
      ...(r.data.summaries || {})
    });
    setPublished({
      ...(r.data.published || {})
    });
  }).catch(() => {});
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);
  if (!doc || !meta || !summaries) return <p className="text-sm text-esaia-subtle">{tr("Loading...")}</p>;
  const patch = async (body, msg) => {
    try {
      await api.patch(`/admin/assessments/${id}`, body);
      if (msg) toast.success(msg);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };
  const toggleChecklist = i => {
    const checklist = doc.checklist.map((c, j) => j === i ? {
      ...c,
      completed: !c.completed
    } : c);
    patch({
      checklist
    }, null);
  };
  const uploadReport = async file => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/admin/assessments/${id}/reports`, fd, {
        headers: {
          "Content-Type": "multipart/form-data"
        }
      });
      toast.success(tr("Management report uploaded."));
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };
  const uploadCert = async e => {
    e.preventDefault();
    const file = certRef.current?.files?.[0];
    if (!file) {
      toast.error(tr("Please select a certificate PDF."));
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    Object.entries(certForm).forEach(([k, v]) => fd.append(k, v));
    try {
      await api.post(`/admin/assessments/${id}/certificates`, fd, {
        headers: {
          "Content-Type": "multipart/form-data"
        }
      });
      toast.success(tr("Certificate uploaded."));
      setCertForm({
        certificate_number: "",
        certificate_type: "INDIVIDUAL",
        start_date: "",
        expiry_date: "",
        status_override: ""
      });
      if (certRef.current) certRef.current.value = "";
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };
  return <div className="mx-auto max-w-6xl" data-testid="admin-assessment-detail">
      <Link to={`/${lang}/admin/clients/${doc.client_id}`} className="group inline-flex items-center gap-2 text-xs font-semibold text-esaia-blue">
        <ArrowLeft size={14} className="transition-transform group-hover:-translate-x-1" />{" "}{tr("Back to")}{" "}{doc.client?.company || "client"}
      </Link>

      <div className="mt-5 border border-esaia-line bg-white p-6 sm:p-8">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-esaia-blue">{doc.assessment_id}</p>
            <h1 className="mt-1 text-xl font-semibold text-esaia-ink sm:text-2xl">
              {doc.service_type ? {
              ERI: "Ethical Readiness Index",
              INDIVIDUAL: "Single-Entity AI Assessment",
              GROUP: "Group-Wide AI Assessment",
              RECERTIFICATION: "Annual ESAIA Recertification"
            }[doc.service_type] : ""}
            </h1>
            <p className="mt-1 text-sm text-esaia-muted">
              {doc.client?.company} · {doc.client?.first_name} {doc.client?.last_name} · {doc.client?.email}
            </p>
          </div>
          <Badge tone={statusTone(doc.status)} testid="admin-assessment-status">{tr(doc.status)}</Badge>
        </div>
        <div className="mt-6 grid grid-cols-1 gap-4 border-t border-esaia-line pt-6 sm:grid-cols-4">
          <Field label={tr("Status")} htmlFor="am-status">
            <select id="am-status" value={meta.status} onChange={e => setMeta({
            ...meta,
            status: e.target.value
          })} className={inputCls} data-testid="am-status">
              {["PENDING", "ACTIVE", "COMPLETED"].map(s => <option key={s} value={s}>{tr(s)}</option>)}
            </select>
          </Field>
          <Field label={tr("Start date")} htmlFor="am-start">
            <input id="am-start" type="date" value={meta.start_date} onChange={e => setMeta({
            ...meta,
            start_date: e.target.value
          })} className={inputCls} data-testid="am-start" />
          </Field>
          <Field label={tr("Completion date")} htmlFor="am-end">
            <input id="am-end" type="date" value={meta.completion_date} onChange={e => setMeta({
            ...meta,
            completion_date: e.target.value
          })} className={inputCls} data-testid="am-end" />
          </Field>
          <div className="flex items-end">
            <button onClick={() => patch(meta, "Assessment updated.")} className="w-full bg-esaia-blue px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-esaia-dark" data-testid="am-save-meta">{tr("Save Changes")}{" "}</button>
          </div>
        </div>
        <div className="mt-4">
          <Field label={tr("Internal notes (never shown to the client)")} htmlFor="am-notes">
            <textarea id="am-notes" rows={2} value={meta.notes} onChange={e => setMeta({
            ...meta,
            notes: e.target.value
          })} className={inputCls} data-testid="am-notes" />
          </Field>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Card title={tr("Assessment Activities")} testid="admin-checklist">
          <ul className="space-y-1">
            {(doc.checklist || []).map((c, i) => <li key={i}>
                <button onClick={() => toggleChecklist(i)} data-testid={`admin-checklist-${i}`} className="flex w-full items-center gap-3 px-2 py-2 text-left text-sm transition-colors hover:bg-esaia-canvas">
                  <span className={`flex h-4.5 w-4.5 h-5 w-5 shrink-0 items-center justify-center border ${c.completed ? "border-esaia-blue bg-esaia-blue text-white" : "border-esaia-line bg-white"}`}>
                    {c.completed && "✓"}
                  </span>
                  <span className={c.completed ? "text-esaia-ink" : "text-esaia-subtle"}>{tr(c.item)}</span>
                </button>
              </li>)}
          </ul>
        </Card>

        <Card title={tr("Management Reports")} testid="admin-reports">
          <input ref={reportRef} type="file" accept="application/pdf,.pdf" className="hidden" onChange={e => uploadReport(e.target.files?.[0])} data-testid="report-file-input" />
          <button onClick={() => reportRef.current?.click()} className="inline-flex items-center gap-2 bg-esaia-blue px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-esaia-dark" data-testid="upload-report-button">
            <Upload size={15} />{" "}{tr("Upload PDF Report")}{" "}</button>
          <ul className="mt-5 divide-y divide-esaia-line">
            {(doc.reports || []).map(r => <li key={r.id} className="flex items-center justify-between gap-3 py-3" data-testid={`admin-report-${r.id}`}>
                <div>
                  <p className="text-sm font-medium text-esaia-ink">{r.file_name}</p>
                  <p className="text-xs text-esaia-subtle">{fmtDateTime(r.uploaded_at)}</p>
                </div>
                <div className="flex gap-2">
                  <a href={fileUrl("reports", r.id, true)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 border border-esaia-line px-3 py-1.5 text-xs font-semibold text-esaia-muted hover:text-esaia-blue">
                    <Eye size={13} />{" "}{tr("View")}{" "}</a>
                  <button onClick={async () => {
                await api.delete(`/admin/reports/${r.id}`);
                toast.success(tr("Report deleted."));
                load();
              }} className="inline-flex items-center gap-1 border border-esaia-line px-3 py-1.5 text-xs font-semibold text-esaia-muted hover:text-red-600" data-testid={`report-delete-${r.id}`}>
                    <Trash2 size={13} />{" "}{tr("Delete")}{" "}</button>
                </div>
              </li>)}
          </ul>
          {(doc.reports || []).length === 0 && <p className="mt-4 text-sm text-esaia-subtle">{tr("No management reports have been uploaded for this assessment.")}</p>}
        </Card>
      </div>

      <div className="mt-6">
        <Card title={tr("Assessment Summary (publish per field to the client portal)")} testid="admin-summaries">
          <div className="space-y-6">
            {SUMMARY_FIELDS.map(([f, label]) => <div key={f}>
                <div className="flex items-center justify-between">
                  <label htmlFor={`sum-${f}`} className="text-xs font-semibold text-esaia-ink">{tr(label)}</label>
                  <label className="flex items-center gap-2 text-xs text-esaia-subtle">
                    <input type="checkbox" checked={!!published[f]} onChange={e => setPublished({
                  ...published,
                  [f]: e.target.checked
                })} className="h-3.5 w-3.5 accent-esaia-blue" data-testid={`publish-${f}`} />{tr("Visible to client")}{" "}</label>
                </div>
                <textarea id={`sum-${f}`} rows={3} value={summaries[f] || ""} onChange={e => setSummaries({
              ...summaries,
              [f]: e.target.value
            })} placeholder={f === "eri_results" ? tr("ERI results: [Admin enters summary]") : ""} className={`mt-2 ${inputCls}`} data-testid={`summary-${f}`} />
              </div>)}
            <button onClick={() => patch({
            summaries,
            published
          }, "Summaries saved.")} className="bg-esaia-blue px-6 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-esaia-dark" data-testid="save-summaries">{tr("Save Changes")}{" "}</button>
          </div>
        </Card>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Card title={tr("Upload Certificate (PDF)")} testid="admin-cert-upload">
          <form onSubmit={uploadCert} className="space-y-4">
            <Field label={tr("Certificate Number")} required htmlFor="cn-number">
              <input id="cn-number" required value={certForm.certificate_number} onChange={e => setCertForm({
              ...certForm,
              certificate_number: e.target.value
            })} placeholder={tr("ESAIA-2026-00124")} className={inputCls} data-testid="cn-number" />
            </Field>
            <Field label={tr("Certification type")} htmlFor="cn-type">
              <select id="cn-type" value={certForm.certificate_type} onChange={e => setCertForm({
              ...certForm,
              certificate_type: e.target.value
            })} className={inputCls} data-testid="cn-type">
                <option value="INDIVIDUAL">{tr("Single-Entity AI Assessment")}</option>
                <option value="GROUP">{tr("Group-Wide AI Assessment")}</option>
                <option value="RECERTIFICATION">{tr("Annual ESAIA Recertification")}</option>
              </select>
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label={tr("Start date")} required htmlFor="cn-start">
                <input id="cn-start" type="date" required value={certForm.start_date} onChange={e => setCertForm({
                ...certForm,
                start_date: e.target.value
              })} className={inputCls} data-testid="cn-start" />
              </Field>
              <Field label={tr("Expiry date")} required htmlFor="cn-expiry">
                <input id="cn-expiry" type="date" required value={certForm.expiry_date} onChange={e => setCertForm({
                ...certForm,
                expiry_date: e.target.value
              })} className={inputCls} data-testid="cn-expiry" />
              </Field>
            </div>
            <Field label={tr("Status (automatic by expiry date; override only if needed)")} htmlFor="cn-status">
              <select id="cn-status" value={certForm.status_override} onChange={e => setCertForm({
              ...certForm,
              status_override: e.target.value
            })} className={inputCls} data-testid="cn-status">
                <option value="">{tr("Automatic")}</option>
                <option value="ACTIVE">{tr("ACTIVE")}</option>
                <option value="EXPIRED">{tr("EXPIRED")}</option>
              </select>
            </Field>
            <Field label={tr("Certificate PDF")} required htmlFor="cn-file">
              <input id="cn-file" ref={certRef} type="file" accept="application/pdf,.pdf" className="w-full text-sm text-esaia-muted file:mr-4 file:border file:border-esaia-line file:bg-white file:px-4 file:py-2 file:text-xs file:font-semibold file:text-esaia-blue" data-testid="cn-file" />
            </Field>
            <button type="submit" className="inline-flex items-center gap-2 bg-esaia-blue px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-esaia-dark" data-testid="cn-submit">
              <Upload size={15} />{" "}{tr("Upload Certificate")}{" "}</button>
          </form>
        </Card>

        <Card title={tr("Certificates")} testid="admin-cert-list">
          {(doc.certificates || []).length === 0 ? <p className="text-sm text-esaia-subtle">{tr("No certificates have been uploaded yet.")}</p> : <ul className="divide-y divide-esaia-line">
              {doc.certificates.map(c => <li key={c.id} className="py-4" data-testid={`admin-cert-${c.certificate_number}`}>
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <p className="text-sm font-semibold text-esaia-ink">{c.certificate_number}</p>
                      <p className="mt-0.5 text-xs text-esaia-subtle">
                        {fmtDate(c.start_date)} — {fmtDate(c.expiry_date)}{" "}{tr("· uploaded")}{" "}{fmtDate(c.uploaded_at)}
                      </p>
                    </div>
                    <Badge tone={c.effective_status === "ACTIVE" ? "green" : "red"}>{tr(c.effective_status)}</Badge>
                  </div>
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    <select value={c.status_override || ""} onChange={async e => {
                await api.patch(`/admin/certificates/${c.id}`, {
                  status_override: e.target.value
                });
                toast.success(tr("Certificate updated."));
                load();
              }} className="border border-esaia-line bg-white px-3 py-1.5 text-xs text-esaia-ink focus:border-esaia-blue focus:outline-none" data-testid={`cert-override-${c.certificate_number}`}>
                      <option value="">{tr("Automatic (")}{c.effective_status})</option>
                      <option value="ACTIVE">{tr("Force ACTIVE")}</option>
                      <option value="EXPIRED">{tr("Force EXPIRED")}</option>
                    </select>
                    <a href={fileUrl("certificates", c.id, true)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 border border-esaia-line px-3 py-1.5 text-xs font-semibold text-esaia-muted hover:text-esaia-blue">
                      <Eye size={13} />{" "}{tr("View")}{" "}</a>
                    <button onClick={async () => {
                await api.delete(`/admin/certificates/${c.id}`);
                toast.success(tr("Certificate deleted."));
                load();
              }} className="inline-flex items-center gap-1 border border-esaia-line px-3 py-1.5 text-xs font-semibold text-esaia-muted hover:text-red-600" data-testid={`cert-delete-${c.certificate_number}`}>
                      <Trash2 size={13} />{" "}{tr("Delete")}{" "}</button>
                  </div>
                </li>)}
            </ul>}
        </Card>
      </div>
    </div>;
}
